import { useCallback, type FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { experiencesActions } from "@ds/modules/experiences/redux/slice";
import {
  selectExperienceById,
  selectExperiences,
  selectExperiencesIsLoading,
  selectSelectedExperienceIds,
} from "@ds/modules/experiences/redux/selectors";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-width: 400px;
`;

export const DuplicateExperienceModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const experienceId = useRootSelector(selectSelectedExperienceIds)[0];
  const experience = useRootSelector(state => selectExperienceById(state, experienceId));
  const experiences = useRootSelector(selectExperiences);
  const experienceNames = experiences.map(_experience => _experience.name.trim().toLowerCase());
  const isLoading = useRootSelector(selectExperiencesIsLoading);
  const dispatch = useRootDispatch();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: experience?.name ? `${experience.name} (Copy)` : "",
    },
  });

  const onSubmit = useCallback(
    (data: { name: string }) => {
      if (experienceId === undefined || experienceId === null) return;
      dispatch(experiencesActions.duplicateExperience({ experienceId, newTitle: data.name }));
      onClose();
    },
    [dispatch, experienceId, onClose],
  );

  const { ref, ...nameRegister } = register("name", {
    required: "Name is required",
    validate: value => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return true;
      const isDuplicate = experienceNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
      return isDuplicate ? "An experience with this name already exists" : true;
    },
  });

  const renderFooter = useCallback(
    () => (
      <>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          isDisabled={isLoading}
          onClick={onClose}
        />
        <Button
          label="Duplicate"
          ariaLabel="duplicate button"
          buttonType={ButtonType.Raised}
          icon={IconType.Duplicate}
          iconPosition="right"
          isLoading={isLoading}
          isDisabled={experienceId === undefined || experienceId === null}
          onClick={handleSubmit(onSubmit)}
        />
      </>
    ),
    [experienceId, isLoading, onClose, handleSubmit, onSubmit],
  );

  return (
    <Dialog modal visible header="Duplicate Experience" footer={renderFooter} onHide={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabelWrapper label="New Experience Name" isRequired error={errors.name}>
          <InputText
            inputRef={ref}
            placeholder="Enter a new name for the duplicated experience"
            {...nameRegister}
            value={watch("name") ?? ""}
          />
        </LabelWrapper>
      </Form>
    </Dialog>
  );
};
